import {
  PointerEventHandler,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAtom } from "jotai";
import {
  PROMPT_DOWNLOAD_SCENARIOS,
  PromptDownloadScenariosType,
} from "../components/DownloadAppPrompt/DownloadAppPrompt";
import useAuth from "./useAuth";
import { useNavigate } from "react-router";
import { ROUTES } from "../utils/constants";
import { FunctionWithVoidReturn } from "../utils/constants/generic-types";
import useCreateGameShareLinkMutation from "../react-query/mutations/use-create-game-share-link";
import useNativeShare from "./useNativeShare";
import { useWindowDimensions } from "./useWindowDimensions";
import { gameShareLinksAtomWithStorage } from "../jotai/atoms/games";
import { GameMediaSummary } from "@nestcoinco/hyper-api-gateway-api-client";
import { visibleGameIndexAtom } from "../jotai/atoms/games";

const DEFAULT_FILTER_ID = 5;

interface UseGamesPageScrollParams {
  scrollContainerRef: RefObject<HTMLElement>;
  defaultVisibleGameIndex?: number;
}

export function useGamesPageScroll({
  scrollContainerRef,
  defaultVisibleGameIndex,
}: UseGamesPageScrollParams) {
  const [visibleGameIndex, setVisibleGameIndex] = useAtom(visibleGameIndexAtom);
  const lastScrollPositionRef = useRef<number>(0);

  const computeVisibleGameIndex = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const { clientHeight, scrollTop: currentScrollPosition } = scrollContainer;
    if (lastScrollPositionRef.current === currentScrollPosition) {
      return;
    }

    const computedIndex = currentScrollPosition / clientHeight;
    const newGameIndex = defaultVisibleGameIndex
      ? computedIndex - 1
      : computedIndex;
    lastScrollPositionRef.current = currentScrollPosition;
    return newGameIndex;
  }, [defaultVisibleGameIndex, scrollContainerRef]);

  const handleScroll = useCallback(() => {
    const scrollEndEventExists = "onscrollend" in window;
    if (scrollEndEventExists) return;

    const newGameIndex = computeVisibleGameIndex();
    // At the point during scroll where the computed index results in an integer/whole number, it signifies that scrolling has ended.
    // We can always be sure of this because the scrollable container always snaps to such a point when scrolling stops, regardless of the point at which the user actually stopped scrolling.
    // This check is useful for browsers that do not support the scrollend event.
    if (newGameIndex !== undefined && Number.isInteger(newGameIndex)) {
      setVisibleGameIndex(newGameIndex);
    }
  }, [computeVisibleGameIndex, setVisibleGameIndex]);

  const handleScrollEnd = useCallback(() => {
    const newGameIndex = computeVisibleGameIndex();
    if (newGameIndex !== undefined) {
      setVisibleGameIndex(Math.round(newGameIndex));
    }
  }, [computeVisibleGameIndex, setVisibleGameIndex]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    scrollContainer.addEventListener("scroll", handleScroll);
    scrollContainer.addEventListener("scrollend", handleScrollEnd);

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
      scrollContainer.removeEventListener("scrollend", handleScrollEnd);
    };
  }, [scrollContainerRef, handleScroll, handleScrollEnd]);

  useEffect(() => {
    if (defaultVisibleGameIndex) setVisibleGameIndex(defaultVisibleGameIndex);
  }, [defaultVisibleGameIndex, setVisibleGameIndex]);

  return {
    visibleGameIndex,
    setVisibleGameIndex,
  };
}

type PromptUserToDownloadApp = (
  promptDownloadScenario: PromptDownloadScenariosType
) => void;

interface UseGamesPageActionsParams {
  playGameRoute?: string;
  visibleVideoFeed?: HTMLVideoElement | null;
  openDownloadAppPrompt: FunctionWithVoidReturn;
}

export function useGamesPageActions({
  playGameRoute = "/",
  openDownloadAppPrompt,
}: UseGamesPageActionsParams) {
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const [isMuted, setIsMuted] = useState<boolean>(true);
  const [selectedFilterId] = useState<number>(DEFAULT_FILTER_ID);
  const [promptDownloadScenario, setPromptDownloadScenario] =
    useState<PromptDownloadScenariosType>(
      isLoggedIn
        ? PROMPT_DOWNLOAD_SCENARIOS.LOGGED_IN.OTHERS
        : PROMPT_DOWNLOAD_SCENARIOS.LOGGED_OUT.OTHERS
    );

  const promptUserToDownloadApp: PromptUserToDownloadApp = (
    downloadScenario
  ) => {
    setPromptDownloadScenario(downloadScenario);
    openDownloadAppPrompt();
  };

  const goToLogin = () => navigate(ROUTES.ROOT.LOG_IN);

  const handleFilterSelection = (filterId: number) => {
    if (filterId === DEFAULT_FILTER_ID) return;
    if (!isLoggedIn) {
      goToLogin();
      return;
    }
    promptUserToDownloadApp(PROMPT_DOWNLOAD_SCENARIOS.LOGGED_IN.OTHERS);
  };

  const handleGenreClick = (genreName: string) => {
    navigate(`${ROUTES.GAMES.GENRES}/${genreName.toLowerCase()}`);
  };

  const handlePlayTournament = () => {
    if (!isLoggedIn) {
      goToLogin();
      return;
    }
    promptUserToDownloadApp(PROMPT_DOWNLOAD_SCENARIOS.LOGGED_IN.PLAY_TOURNAMENT);
  };

  const handlePlayWithFriends = () => {
    if (!isLoggedIn) {
      goToLogin();
      return;
    }
    promptUserToDownloadApp(
      PROMPT_DOWNLOAD_SCENARIOS.LOGGED_IN.PLAY_WITH_FRIENDS
    );
  };

  const goToPlayGame = () => {
    if (!isLoggedIn) {
      goToLogin();
      return;
    }
    navigate(playGameRoute);
  };

  const toggleMute = useCallback((muteStatus?: boolean) => {
    setIsMuted((prevState) => muteStatus ?? !prevState);
  }, []);

  return {
    isMuted,
    selectedFilterId,
    promptDownloadScenario,
    promptUserToDownloadApp,
    handleFilterSelection,
    handleGenreClick,
    handlePlayTournament,
    handlePlayWithFriends,
    toggleMute,
    goToPlayGame,
  };
}

interface UseShareGameLinkActionParams {
  gameId?: string;
  desktopShareHandler?: FunctionWithVoidReturn;
}

export function useShareGameLinkAction({
  gameId,
  desktopShareHandler,
}: UseShareGameLinkActionParams) {
  const openShareTray = useNativeShare();
  const { width: windowWidth } = useWindowDimensions();
  const isDesktop = windowWidth >= 1024;
  const {
    mutate: createShareLink,
    data: gameShareLink,
    isPending: isCreateShareLinkPending,
    isSuccess: isCreateShareLinkSuccessful,
    reset: resetCreateShareLinkMutation,
  } = useCreateGameShareLinkMutation();
  const [gameShareLinks, setGameShareLinks] = useAtom(
    gameShareLinksAtomWithStorage
  );

  const handleShare = useCallback(() => {
    if (!gameShareLinks || !gameId) return;

    const link = gameShareLinks[gameId];

    if (isDesktop) {
      desktopShareHandler?.();
    } else {
      openShareTray({
        title: "Share Game on Hyper",
        text: link.link,
      });
    }
  }, [desktopShareHandler, gameId, gameShareLinks, isDesktop, openShareTray]);

  const currentLink = useMemo(
    () => (gameShareLinks && gameId ? gameShareLinks[gameId] : undefined),
    [gameId, gameShareLinks]
  );

  useEffect(() => {
    if (
      gameId &&
      ((gameShareLinks && !gameShareLinks[gameId]) || !gameShareLinks)
    ) {
      createShareLink(gameId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId]);

  useEffect(() => {
    if (!isCreateShareLinkSuccessful || !gameId) return;

    setGameShareLinks((prev) => ({
      ...(prev || {}),
      [gameId]: gameShareLink,
    }));
    resetCreateShareLinkMutation();
  }, [
    gameId,
    gameShareLink,
    isCreateShareLinkSuccessful,
    resetCreateShareLinkMutation,
    setGameShareLinks,
  ]);

  return {
    isDesktop,
    isCreateShareLinkPending,
    gameShareLink: currentLink,
    handleShare,
  };
}

interface UseGameMediaSelectionParams {
  gameId?: string;
  gameMedia?: GameMediaSummary[];
}

export function useGameMediaSelection({
  gameId,
  gameMedia,
}: UseGameMediaSelectionParams) {
  const [selectedGamesMedia, setSelectedGamesMedia] = useState<
    Record<string, number>
  >({});
  const mediaIndexExists = gameId
    ? selectedGamesMedia[gameId] !== undefined
    : false;

  const selectedMediaIndex = useMemo(() => {
    if (!gameMedia || !gameMedia.length) return;

    if (gameId && mediaIndexExists) {
      return selectedGamesMedia[gameId];
    }

    const mediaIndex = Math.floor(Math.random() * gameMedia.length);
    return mediaIndex;
  }, [gameId, gameMedia, mediaIndexExists, selectedGamesMedia]);

  // Update the list of selected game media indices when the computation for the selected game media is done
  useEffect(() => {
    if (!gameId || selectedMediaIndex === undefined) return;

    if (!mediaIndexExists) {
      setSelectedGamesMedia((prev) => ({
        ...prev,
        [gameId]: selectedMediaIndex,
      }));
    }
  }, [gameId, mediaIndexExists, selectedMediaIndex, setSelectedGamesMedia]);

  return selectedMediaIndex;
}

export function useVideoSeek(video: HTMLVideoElement | null) {
  const isDragging = useRef<boolean>(false);
  const seekButtonRef = useRef<HTMLButtonElement | null>(null);

  const seek = useCallback(
    (clientX: number) => {
      const seekButton = seekButtonRef.current;
      if (!video || !seekButton) return;

      const rect = seekButton.getBoundingClientRect();
      let pos = (clientX - rect.left) / rect.width;
      pos = Math.max(0, Math.min(1, pos)); // clamp between 0 and 1
      video.currentTime = pos * video.duration;
    },
    [video]
  );

  const handlePointerDown: PointerEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      if (!video) return;

      // Prevent the default scroll behaviour for touch events
      if (e.pointerType === "touch") {
        e.preventDefault();
      }

      video.pause();
      isDragging.current = true;
      seekButtonRef.current = e.currentTarget;
      seek(e.clientX);

      const handlePointerMove = (moveEvent: PointerEvent) => {
        if (isDragging.current) {
          seek(moveEvent.clientX);
        }
      };

      const handlePointerUp = () => {
        video.play();
        isDragging.current = false;
        seekButtonRef.current = null;
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerUp);
      };

      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
    },
    [seek, video]
  );

  return handlePointerDown;
}
